import { useEffect, useState } from 'react';
import { Card, withStyles } from 'vopay';
import { Title, UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import SuperAdminDetails from './SuperAdminDetails';
import SuperAdminEdit from './SuperAdminEdit';
import styleSheet from './SuperAdmins.module.scss';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

const cn = withStyles(styleSheet);

interface SuperAdminsProps {
  isDisabled?: boolean;
  onEdit: (isEditing: boolean) => void;
}

const SuperAdmins = ({ isDisabled = false, onEdit }: SuperAdminsProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Roles.SuperAdmin' });
  const { response, fetchUserRoles } = useFetchUserRoles();
  const [isEditing, setIsEditing] = useState(false);
  const [, setLoading] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        setLoading(true);
        await fetchUserRoles({ Role: 'super admin' });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('loadErrorMessage', { defaultValue: 'Failed to load super admins. Please try again.' });
        await alertDialog.error(t('loadErrorTitle', { defaultValue: 'Error' }), errorMessage, {
          abortLabel: t('closeButton', { defaultValue: 'Close' }),
        });
      } finally {
        setLoading(false);
      }
    };

    void loadUserRoles();
  }, []);

  const superAdmins = response?.UserRolePermissions || [];

  const handleEdit = () => {
    setIsEditing(true);
    onEdit(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    onEdit(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Refresh the list after save
      await fetchUserRoles({ Role: 'super admin' });
      setIsEditing(false);
      onEdit(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('refreshErrorMessage', { defaultValue: 'Failed to refresh super admins. Please reload the page.' });
      await alertDialog.error(t('refreshErrorTitle', { defaultValue: 'Error' }), errorMessage, {
        abortLabel: t('closeButton', { defaultValue: 'Close' }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={cn('card')} style={{ opacity: isDisabled ? 0.5 : 1 }}>
      <div className={cn('header')}>
        <Title level="4">{t('title', { defaultValue: 'Super Admins' })}</Title>
        {!isEditing && (
          <UtilButton onClick={handleEdit} iconLeft="edit" disabled={isDisabled}>
            {t('editButton', { defaultValue: 'Edit Super Admins' })}
          </UtilButton>
        )}
      </div>

      {isEditing ? (
        <SuperAdminEdit superAdmins={superAdmins} onCancel={handleCancel} onSave={handleSave} />
      ) : (
        <SuperAdminDetails superAdmins={superAdmins} />
      )}
    </Card>
  );
};

export default SuperAdmins;
